import React from "react";
import SEO from "../components/SEO";
import DropMenu from "../components/DropMenu";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Headshot from "../images/SPC_CIRHS-SM.png";
import Logo from "../images/ShiftITLogo.png";
import { Page, Section, Container } from "../components/layout/primitives";
import styled from "styled-components";

const CardSection = styled(Section)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 120px;
  padding-bottom: 60px;
`;

const Card = styled.div`
  max-width: 440px;
  width: 100%;
  border-radius: 24px;
  background: radial-gradient(circle at top, #1a2736, #0e1a24);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.55);
  padding: 28px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LogoImg = styled.img`
  height: 32px;
  width: auto;
  padding-right: 8px;
`;

const Tag = styled.span`
  font-size: 12px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(247, 248, 250, 0.7);
`;

const IdentityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const Avatar = styled.img`
  width: 90px;
  height: 90px;
  border-radius: 999px;
  border: 2px solid rgba(255, 203, 102, 0.8);
  object-fit: cover;
`;

const NameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Name = styled.h1`
  font-size: 22px;
  line-height: 28px;
  margin: 0;
  color: #f7f8fa;
`;

const Title = styled.p`
  margin: 0;
  font-size: 16px;
  color: rgba(247, 248, 250, 0.78);
`;

const Org = styled.p`
  margin: 0;
  font-size: 16px;
  color: rgba(247, 248, 250, 0.78);
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid rgba(247, 248, 250, 0.1);
  margin: 8px 0 4px;
`;

const InfoList = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 10px;
  row-gap: 8px;
  font-size: 14px;
`;

const Label = styled.dt`
  color: rgba(247, 248, 250, 0.6);
`;

const Value = styled.dd`
  margin: 0;
  color: #f7f8fa;
  word-break: break-word;
`;

const Link = styled.a`
  color: #ffcb66;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 999px;
  background: #ffcb66;
  color: #0e1a24;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  border: none;
  cursor: pointer;

  &:hover {
    background: #ffd88a;
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 999px;
  background: transparent;
  color: #f7f8fa;
  font-weight: 500;
  font-size: 14px;
  text-decoration: none;
  border: 1px solid rgba(247, 248, 250, 0.3);
  cursor: pointer;

  &:hover {
    border-color: rgba(247, 248, 250, 0.6);
  }
`;

export default function ContactPage() {
  const [isOpen, setIsOpen] = React.useState(false);
  const toggle = () => setIsOpen(!isOpen);

  return (
    <>
      <SEO
        title="Contact | Shift IT"
        description="Digital contact card for Sean P. Cook, Owner of Shift IT in Atlanta, GA. Save contact details or connect via email, phone, website, and LinkedIn."
        canonical="https://shift-it.io/contact"
      />

      <DropMenu isOpen={isOpen} toggle={toggle} />
      <Navbar toggle={toggle} />

      <Page>
        <CardSection>
          <Container>
            <Card>
              <LogoRow>
                <LogoImg src={Logo} alt="Shift IT" />
                <Tag>Digital contact card</Tag>
              </LogoRow>

              <IdentityRow>
                <Avatar src={Headshot} alt="Sean P. Cook" />
                <NameBlock>
                  <Name>Sean P. Cook</Name>
                  <Title>Owner</Title>
                  <Org>Shift IT</Org>
                </NameBlock>
              </IdentityRow>

              <Divider />

              <InfoList>
                <Label>Email</Label>
                <Value>
                  <Link href="mailto:sean@shift-it.io">sean@shift-it.io</Link>
                </Value>

                <Label>Phone</Label>
                <Value>
                  <Link href="tel:+14047792188">(404) 779-2188</Link>
                </Value>

                <Label>Website</Label>
                <Value>
                  <Link
                    href="https://shift-it.io"
                    target="_blank"
                    rel="noreferrer"
                  >
                    shift-it.io
                  </Link>
                </Value>

                <Label>LinkedIn</Label>
                <Value>
                  <Link
                    href="https://www.linkedin.com/in/seanpcook-shiftit"
                    target="_blank"
                    rel="noreferrer"
                  >
                    linkedin.com/in/seanpcook-shiftit
                  </Link>
                </Value>

                <Label>Company</Label>
                <Value>
                  <Link
                    href="https://www.linkedin.com/company/shift-it-io/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    linkedin.com/company/shift-it-io/
                  </Link>
                </Value>
              </InfoList>

              <Actions>
                <PrimaryButton href="/sean-p-cook.vcf" download>
                  Download contact card
                </PrimaryButton>
                <SecondaryButton href="mailto:sean@shift-it.io">
                  Email Sean
                </SecondaryButton>
                <SecondaryButton
                  href="https://outlook.office.com/bookwithme/user/bb51bbe3bc3d4e3eb0635e8990c6ad0b@shift-it.io?anonymous&ep=plink"
                  target="_blank"
                  rel="noreferrer"
                >
                  Book time with Sean
                </SecondaryButton>
              </Actions>
            </Card>
          </Container>
        </CardSection>
      </Page>

      <Footer />
    </>
  );
}
